import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Heart, Star, ChevronRight, Gift, DollarSign, Wallet, CreditCard, ArrowRight } from "lucide-react";

const MiniLogo = ({ name, size = "md" }) => {
  const colors = ["bg-sky-400", "bg-purple-400", "bg-green-400", "bg-orange-400", "bg-pink-400", "bg-indigo-400", "bg-teal-400", "bg-red-400"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  const sizeClass = size === "sm" ? "w-8 h-8 text-xs" : "w-10 h-10 text-sm";
  return (
    <div className={`${sizeClass} rounded-xl ${colors[i]} flex items-center justify-center flex-shrink-0`}>
      <span className="text-white font-bold">{name?.charAt(0)?.toUpperCase()}</span>
    </div>
  );
};

const CardStack = ({ title, offers, color, onOfferClick, favoriteIds, toggleFavorite }) => {
  const [expanded, setExpanded] = useState(false);
  const topOffers = expanded ? offers : offers.slice(0, 3);
  const totalValue = offers.reduce((s, o) => s + (o.offer_value_est || 0), 0);

  return (
    <div className="mb-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-3 group"
        data-testid={`wallet-stack-${title.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-bold text-slate-700" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
          <span className="text-xs text-slate-400">{offers.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-green-600">${totalValue.toFixed(0)}</span>
          <ChevronRight className={`w-4 h-4 text-slate-300 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </div>
      </button>

      <div className="relative">
        {!expanded && offers.length > 3 && (
          <>
            <div className="absolute -bottom-1.5 left-2 right-2 h-8 bg-slate-100 rounded-xl opacity-60" />
            <div className="absolute -bottom-3 left-4 right-4 h-8 bg-slate-200 rounded-xl opacity-40" />
          </>
        )}
        <AnimatePresence mode="popLayout">
          {topOffers.map((offer, i) => (
            <motion.div
              key={offer.business_id}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ delay: i * 0.04 }}
              onClick={() => onOfferClick(offer)}
              className="bg-white rounded-xl p-3.5 border border-slate-100 shadow-[0_2px_12px_rgba(0,0,0,0.05)] mb-2.5 cursor-pointer hover:shadow-md transition-all flex items-center gap-3"
              data-testid={`wallet-offer-${offer.business_id}`}
            >
              <MiniLogo name={offer.name} />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm truncate">{offer.name}</p>
                {getStatus(offer.business_id) && <UnifiedStatusBadge status={getStatus(offer.business_id)} />}
                <p className="text-xs text-slate-400 truncate">{offer.offer_title}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-green-600">${offer.offer_value_est}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleFavorite(offer.business_id); }}
                  className="p-1"
                >
                  <Heart className={`w-3.5 h-3.5 ${favoriteIds.has(offer.business_id) ? "fill-red-500 text-red-500" : "text-slate-200"}`} />
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export const WalletView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, bestValue, stats, favoriteIds, toggleFavorite, searchQuery, setSearchQuery, categories } = data;

  const totalValue = offers.reduce((sum, o) => sum + (o.offer_value_est || 0), 0);

  // Group by category
  const grouped = {};
  offers.forEach(offer => {
    const cat = offer.category || "Other";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(offer);
  });
  const stacks = Object.entries(grouped).sort((a, b) => {
    const valA = a[1].reduce((s, o) => s + (o.offer_value_est || 0), 0);
    const valB = b[1].reduce((s, o) => s + (o.offer_value_est || 0), 0);
    return valB - valA;
  });

  return (
    <div className="min-h-screen bg-slate-900 pb-28" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Dark premium hero — the "wallet" card */}
      <div className="px-6 pt-8 pb-2">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-br from-sky-500 to-indigo-600 rounded-3xl p-6 shadow-[0_20px_60px_rgba(14,165,233,0.3)] relative overflow-hidden"
        >
          {/* Decorative circles */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/5 rounded-full" />

          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-white/70" />
              <p className="text-sky-100 text-xs font-medium tracking-widest uppercase">Birthday Wallet</p>
            </div>
            <h1 className="text-5xl font-extrabold text-white mt-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              ${totalValue.toFixed(0)}
            </h1>
            <p className="text-sky-200 text-sm mt-1">potential birthday haul</p>

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/20">
              <div>
                <p className="text-white/60 text-xs">Deals</p>
                <p className="text-white font-bold text-lg">{offers.length}</p>
              </div>
              <div>
                <p className="text-white/60 text-xs">Categories</p>
                <p className="text-white font-bold text-lg">{stacks.length}</p>
              </div>
              <div>
                <p className="text-white/60 text-xs">Days Left</p>
                <p className="text-white font-bold text-lg">{stats.days_until_birthday}</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Search */}
      <div className="px-6 mt-4 mb-6">
        <div className="bg-slate-800 rounded-xl flex items-center gap-2 px-4 py-3 border border-slate-700">
          <Search className="w-4 h-4 text-slate-500" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search your wallet..."
            className="flex-1 bg-transparent text-sm text-white outline-none placeholder-slate-500"
            data-testid="wallet-search"
          />
        </div>
      </div>

      {/* Top picks row */}
      {bestValue.length > 0 && (
        <div className="px-6 mb-6">
          <h2 className="text-xs font-bold text-slate-400 tracking-widest uppercase mb-3">Top Picks</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 scrollbar-hide">
            {bestValue.slice(0, 6).map(offer => (
              <motion.div
                key={offer.business_id}
                whileTap={{ scale: 0.95 }}
                onClick={() => onOfferClick(offer)}
                className="flex-shrink-0 w-28 bg-slate-800 rounded-2xl p-3 border border-slate-700 cursor-pointer hover:border-sky-500/50 transition-colors"
                data-testid={`wallet-top-${offer.business_id}`}
              >
                <MiniLogo name={offer.name} size="sm" />
                <p className="text-white font-semibold text-xs mt-2 truncate">{offer.name}</p>
                <p className="text-green-400 font-bold text-sm">${offer.offer_value_est}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Category stacks */}
      <div className="px-6">
        <h2 className="text-xs font-bold text-slate-400 tracking-widest uppercase mb-4">Your Stacks</h2>
        {stacks.map(([cat, catOffers]) => (
          <CardStack
            key={cat}
            title={cat}
            offers={catOffers}
            onOfferClick={onOfferClick}
            favoriteIds={favoriteIds}
            toggleFavorite={toggleFavorite}
          />
        ))}
      </div>
    </div>
  );
};
